import type { Beast, ChallengeRating } from '@character-creator/types';
import { CR_VALUES } from '@character-creator/types';

/**
 * SRD 5.1 Beasts
 * Includes beasts commonly used for Wild Shape and Find Familiar.
 * Organized by Challenge Rating.
 */
export const beasts: Beast[] = [
  // ============================================
  // CR 0 Beasts (Familiars & Low-level Wild Shape)
  // ============================================
  {
    id: 'bat',
    name: 'Bat',
    size: 'tiny',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 12,
    hitPoints: { average: 1, formula: '1d4 - 1' },
    speed: { walk: 5, fly: 30 },
    abilityScores: {
      strength: 2,
      dexterity: 15,
      constitution: 8,
      intelligence: 2,
      wisdom: 12,
      charisma: 4,
    },
    challengeRating: '0',
    experiencePoints: 10,
    senses: ['Blindsight 60 ft.'],
    traits: [
      {
        name: 'Echolocation',
        description: "The bat can't use its blindsight while deafened.",
      },
      {
        name: 'Keen Hearing',
        description: 'The bat has advantage on Wisdom (Perception) checks that rely on hearing.',
      },
    ],
    actions: [
      {
        name: 'Bite',
        description: 'Melee Weapon Attack: +0 to hit, reach 5 ft., one creature.',
        attackBonus: 0,
        damage: '1 piercing',
        damageType: 'piercing',
      },
    ],
    canBeFamiliar: true,
    wildShapeEligible: true,
    source: 'srd-5.1',
  },
  {
    id: 'cat',
    name: 'Cat',
    size: 'tiny',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 12,
    hitPoints: { average: 2, formula: '1d4' },
    speed: { walk: 40, climb: 30 },
    abilityScores: {
      strength: 3,
      dexterity: 15,
      constitution: 10,
      intelligence: 3,
      wisdom: 12,
      charisma: 7,
    },
    challengeRating: '0',
    experiencePoints: 10,
    senses: ['Darkvision 30 ft.'],
    traits: [
      {
        name: 'Keen Smell',
        description: 'The cat has advantage on Wisdom (Perception) checks that rely on smell.',
      },
    ],
    actions: [
      {
        name: 'Claws',
        description: 'Melee Weapon Attack: +0 to hit, reach 5 ft., one target.',
        attackBonus: 0,
        damage: '1 slashing',
        damageType: 'slashing',
      },
    ],
    canBeFamiliar: true,
    wildShapeEligible: true,
    source: 'srd-5.1',
  },
  {
    id: 'crab',
    name: 'Crab',
    size: 'tiny',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 11,
    hitPoints: { average: 2, formula: '1d4' },
    speed: { walk: 20, swim: 20 },
    abilityScores: {
      strength: 2,
      dexterity: 11,
      constitution: 10,
      intelligence: 1,
      wisdom: 8,
      charisma: 2,
    },
    challengeRating: '0',
    experiencePoints: 10,
    senses: ['Blindsight 30 ft.'],
    traits: [
      {
        name: 'Amphibious',
        description: 'The crab can breathe air and water.',
      },
    ],
    actions: [
      {
        name: 'Claw',
        description: 'Melee Weapon Attack: +0 to hit, reach 5 ft., one target.',
        attackBonus: 0,
        damage: '1 bludgeoning',
        damageType: 'bludgeoning',
      },
    ],
    canBeFamiliar: true,
    wildShapeEligible: true,
    source: 'srd-5.1',
  },
  {
    id: 'frog',
    name: 'Frog',
    size: 'tiny',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 11,
    hitPoints: { average: 1, formula: '1d4 - 1' },
    speed: { walk: 20, swim: 20 },
    abilityScores: {
      strength: 1,
      dexterity: 13,
      constitution: 8,
      intelligence: 1,
      wisdom: 8,
      charisma: 3,
    },
    challengeRating: '0',
    experiencePoints: 0,
    senses: ['Darkvision 30 ft.'],
    traits: [
      {
        name: 'Amphibious',
        description: 'The frog can breathe air and water.',
      },
      {
        name: 'Standing Leap',
        description:
          "The frog's long jump is up to 10 feet and its high jump is up to 5 feet, with or without a running start.",
      },
    ],
    canBeFamiliar: true,
    wildShapeEligible: true,
    source: 'srd-5.1',
  },
  {
    id: 'hawk',
    name: 'Hawk',
    size: 'tiny',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 13,
    hitPoints: { average: 1, formula: '1d4 - 1' },
    speed: { walk: 10, fly: 60 },
    abilityScores: {
      strength: 5,
      dexterity: 16,
      constitution: 8,
      intelligence: 2,
      wisdom: 14,
      charisma: 6,
    },
    challengeRating: '0',
    experiencePoints: 10,
    traits: [
      {
        name: 'Keen Sight',
        description: 'The hawk has advantage on Wisdom (Perception) checks that rely on sight.',
      },
    ],
    actions: [
      {
        name: 'Talons',
        description: 'Melee Weapon Attack: +5 to hit, reach 5 ft., one target.',
        attackBonus: 5,
        damage: '1 slashing',
        damageType: 'slashing',
      },
    ],
    canBeFamiliar: true,
    wildShapeEligible: true,
    source: 'srd-5.1',
  },
  {
    id: 'lizard',
    name: 'Lizard',
    size: 'tiny',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 10,
    hitPoints: { average: 2, formula: '1d4' },
    speed: { walk: 20, climb: 20 },
    abilityScores: {
      strength: 2,
      dexterity: 11,
      constitution: 10,
      intelligence: 1,
      wisdom: 8,
      charisma: 3,
    },
    challengeRating: '0',
    experiencePoints: 10,
    senses: ['Darkvision 30 ft.'],
    actions: [
      {
        name: 'Bite',
        description: 'Melee Weapon Attack: +0 to hit, reach 5 ft., one target.',
        attackBonus: 0,
        damage: '1 piercing',
        damageType: 'piercing',
      },
    ],
    canBeFamiliar: true,
    wildShapeEligible: true,
    source: 'srd-5.1',
  },
  {
    id: 'octopus',
    name: 'Octopus',
    size: 'small',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 12,
    hitPoints: { average: 3, formula: '1d6' },
    speed: { walk: 5, swim: 30 },
    abilityScores: {
      strength: 4,
      dexterity: 15,
      constitution: 11,
      intelligence: 3,
      wisdom: 10,
      charisma: 4,
    },
    challengeRating: '0',
    experiencePoints: 10,
    senses: ['Darkvision 30 ft.'],
    traits: [
      {
        name: 'Hold Breath',
        description: 'While out of water, the octopus can hold its breath for 30 minutes.',
      },
      {
        name: 'Underwater Camouflage',
        description:
          'The octopus has advantage on Dexterity (Stealth) checks made while underwater.',
      },
      {
        name: 'Water Breathing',
        description: 'The octopus can breathe only underwater.',
      },
    ],
    actions: [
      {
        name: 'Tentacles',
        description:
          'Melee Weapon Attack: +4 to hit, reach 5 ft., one target. The target is grappled (escape DC 10).',
        attackBonus: 4,
        damage: '1 bludgeoning',
        damageType: 'bludgeoning',
      },
      {
        name: 'Ink Cloud',
        description:
          'A 5-foot-radius cloud of ink extends all around the octopus if it is underwater. The area is heavily obscured for 1 minute.',
      },
    ],
    canBeFamiliar: true,
    wildShapeEligible: true,
    source: 'srd-5.1',
  },
  {
    id: 'owl',
    name: 'Owl',
    size: 'tiny',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 11,
    hitPoints: { average: 1, formula: '1d4 - 1' },
    speed: { walk: 5, fly: 60 },
    abilityScores: {
      strength: 3,
      dexterity: 13,
      constitution: 8,
      intelligence: 2,
      wisdom: 12,
      charisma: 7,
    },
    challengeRating: '0',
    experiencePoints: 10,
    senses: ['Darkvision 120 ft.'],
    traits: [
      {
        name: 'Flyby',
        description: "The owl doesn't provoke opportunity attacks when it flies out of an enemy's reach.",
      },
      {
        name: 'Keen Hearing and Sight',
        description:
          'The owl has advantage on Wisdom (Perception) checks that rely on hearing or sight.',
      },
    ],
    actions: [
      {
        name: 'Talons',
        description: 'Melee Weapon Attack: +3 to hit, reach 5 ft., one target.',
        attackBonus: 3,
        damage: '1 slashing',
        damageType: 'slashing',
      },
    ],
    canBeFamiliar: true,
    wildShapeEligible: true,
    source: 'srd-5.1',
  },
  {
    id: 'poisonous-snake',
    name: 'Poisonous Snake',
    size: 'tiny',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 13,
    hitPoints: { average: 2, formula: '1d4' },
    speed: { walk: 30, swim: 30 },
    abilityScores: {
      strength: 2,
      dexterity: 16,
      constitution: 11,
      intelligence: 1,
      wisdom: 10,
      charisma: 3,
    },
    challengeRating: '1/8',
    experiencePoints: 25,
    senses: ['Blindsight 10 ft.'],
    actions: [
      {
        name: 'Bite',
        description:
          'Melee Weapon Attack: +5 to hit, reach 5 ft., one target. Hit: 1 piercing damage, and the target must make a DC 10 Constitution saving throw, taking 5 (2d4) poison damage on a failed save, or half as much on a successful one.',
        attackBonus: 5,
        damage: '1 piercing + 2d4 poison',
        damageType: 'piercing',
      },
    ],
    canBeFamiliar: true,
    wildShapeEligible: true,
    source: 'srd-5.1',
  },
  {
    id: 'quipper',
    name: 'Quipper',
    size: 'tiny',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 13,
    hitPoints: { average: 1, formula: '1d4 - 1' },
    speed: { swim: 40 },
    abilityScores: {
      strength: 2,
      dexterity: 16,
      constitution: 9,
      intelligence: 1,
      wisdom: 7,
      charisma: 2,
    },
    challengeRating: '0',
    experiencePoints: 10,
    senses: ['Darkvision 60 ft.'],
    traits: [
      {
        name: 'Blood Frenzy',
        description:
          "The quipper has advantage on melee attack rolls against any creature that doesn't have all its hit points.",
      },
      {
        name: 'Water Breathing',
        description: 'The quipper can breathe only underwater.',
      },
    ],
    actions: [
      {
        name: 'Bite',
        description: 'Melee Weapon Attack: +5 to hit, reach 5 ft., one target.',
        attackBonus: 5,
        damage: '1 piercing',
        damageType: 'piercing',
      },
    ],
    canBeFamiliar: true,
    wildShapeEligible: true,
    source: 'srd-5.1',
  },
  {
    id: 'rat',
    name: 'Rat',
    size: 'tiny',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 10,
    hitPoints: { average: 1, formula: '1d4 - 1' },
    speed: { walk: 20 },
    abilityScores: {
      strength: 2,
      dexterity: 11,
      constitution: 9,
      intelligence: 2,
      wisdom: 10,
      charisma: 4,
    },
    challengeRating: '0',
    experiencePoints: 10,
    senses: ['Darkvision 30 ft.'],
    traits: [
      {
        name: 'Keen Smell',
        description: 'The rat has advantage on Wisdom (Perception) checks that rely on smell.',
      },
    ],
    actions: [
      {
        name: 'Bite',
        description: 'Melee Weapon Attack: +0 to hit, reach 5 ft., one target.',
        attackBonus: 0,
        damage: '1 piercing',
        damageType: 'piercing',
      },
    ],
    canBeFamiliar: true,
    wildShapeEligible: true,
    source: 'srd-5.1',
  },
  {
    id: 'raven',
    name: 'Raven',
    size: 'tiny',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 12,
    hitPoints: { average: 1, formula: '1d4 - 1' },
    speed: { walk: 10, fly: 50 },
    abilityScores: {
      strength: 2,
      dexterity: 14,
      constitution: 8,
      intelligence: 2,
      wisdom: 12,
      charisma: 6,
    },
    challengeRating: '0',
    experiencePoints: 10,
    traits: [
      {
        name: 'Mimicry',
        description:
          "The raven can mimic simple sounds it has heard. A creature that hears the sounds can tell they are imitations with a successful DC 10 Wisdom (Insight) check.",
      },
    ],
    actions: [
      {
        name: 'Beak',
        description: 'Melee Weapon Attack: +4 to hit, reach 5 ft., one target.',
        attackBonus: 4,
        damage: '1 piercing',
        damageType: 'piercing',
      },
    ],
    canBeFamiliar: true,
    wildShapeEligible: true,
    source: 'srd-5.1',
  },
  {
    id: 'sea-horse',
    name: 'Sea Horse',
    size: 'tiny',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 11,
    hitPoints: { average: 1, formula: '1d4 - 1' },
    speed: { swim: 20 },
    abilityScores: {
      strength: 1,
      dexterity: 12,
      constitution: 8,
      intelligence: 1,
      wisdom: 10,
      charisma: 2,
    },
    challengeRating: '0',
    experiencePoints: 0,
    traits: [
      {
        name: 'Water Breathing',
        description: 'The sea horse can breathe only underwater.',
      },
    ],
    canBeFamiliar: true,
    wildShapeEligible: true,
    source: 'srd-5.1',
  },
  {
    id: 'spider',
    name: 'Spider',
    size: 'tiny',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 12,
    hitPoints: { average: 1, formula: '1d4 - 1' },
    speed: { walk: 20, climb: 20 },
    abilityScores: {
      strength: 2,
      dexterity: 14,
      constitution: 8,
      intelligence: 1,
      wisdom: 10,
      charisma: 2,
    },
    challengeRating: '0',
    experiencePoints: 10,
    senses: ['Darkvision 30 ft.'],
    traits: [
      {
        name: 'Spider Climb',
        description:
          'The spider can climb difficult surfaces, including upside down on ceilings, without needing to make an ability check.',
      },
      {
        name: 'Web Sense',
        description:
          'While in contact with a web, the spider knows the exact location of any other creature in contact with the same web.',
      },
      {
        name: 'Web Walker',
        description: 'The spider ignores movement restrictions caused by webbing.',
      },
    ],
    actions: [
      {
        name: 'Bite',
        description:
          'Melee Weapon Attack: +4 to hit, reach 5 ft., one creature. The target must succeed on a DC 9 Constitution saving throw or take 2 (1d4) poison damage.',
        attackBonus: 4,
        damage: '1 piercing + 1d4 poison',
        damageType: 'piercing',
      },
    ],
    canBeFamiliar: true,
    wildShapeEligible: true,
    source: 'srd-5.1',
  },
  {
    id: 'weasel',
    name: 'Weasel',
    size: 'tiny',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 13,
    hitPoints: { average: 1, formula: '1d4 - 1' },
    speed: { walk: 30 },
    abilityScores: {
      strength: 3,
      dexterity: 16,
      constitution: 8,
      intelligence: 2,
      wisdom: 12,
      charisma: 3,
    },
    challengeRating: '0',
    experiencePoints: 10,
    traits: [
      {
        name: 'Keen Hearing and Smell',
        description:
          'The weasel has advantage on Wisdom (Perception) checks that rely on hearing or smell.',
      },
    ],
    actions: [
      {
        name: 'Bite',
        description: 'Melee Weapon Attack: +5 to hit, reach 5 ft., one target.',
        attackBonus: 5,
        damage: '1 piercing',
        damageType: 'piercing',
      },
    ],
    canBeFamiliar: true,
    wildShapeEligible: true,
    source: 'srd-5.1',
  },

  // ============================================
  // CR 1/4 Beasts (Level 2 Wild Shape)
  // ============================================
  {
    id: 'wolf',
    name: 'Wolf',
    size: 'medium',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 13,
    hitPoints: { average: 11, formula: '2d8 + 2' },
    speed: { walk: 40 },
    abilityScores: {
      strength: 12,
      dexterity: 15,
      constitution: 12,
      intelligence: 3,
      wisdom: 12,
      charisma: 6,
    },
    challengeRating: '1/4',
    experiencePoints: 50,
    traits: [
      {
        name: 'Keen Hearing and Smell',
        description:
          'The wolf has advantage on Wisdom (Perception) checks that rely on hearing or smell.',
      },
      {
        name: 'Pack Tactics',
        description:
          "The wolf has advantage on attack rolls against a creature if at least one of the wolf's allies is within 5 feet of the creature and the ally isn't incapacitated.",
      },
    ],
    actions: [
      {
        name: 'Bite',
        description:
          'Melee Weapon Attack: +4 to hit, reach 5 ft., one target. Hit: 7 (2d4 + 2) piercing damage. If the target is a creature, it must succeed on a DC 11 Strength saving throw or be knocked prone.',
        attackBonus: 4,
        damage: '2d4 + 2',
        damageType: 'piercing',
      },
    ],
    wildShapeEligible: true,
    source: 'srd-5.1',
  },
  {
    id: 'boar',
    name: 'Boar',
    size: 'medium',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 11,
    hitPoints: { average: 11, formula: '2d8 + 2' },
    speed: { walk: 40 },
    abilityScores: {
      strength: 13,
      dexterity: 11,
      constitution: 12,
      intelligence: 2,
      wisdom: 9,
      charisma: 5,
    },
    challengeRating: '1/4',
    experiencePoints: 50,
    traits: [
      {
        name: 'Charge',
        description:
          'If the boar moves at least 20 feet straight toward a target and then hits it with a tusk attack on the same turn, the target takes an extra 3 (1d6) slashing damage. If the target is a creature, it must succeed on a DC 11 Strength saving throw or be knocked prone.',
      },
      {
        name: 'Relentless',
        description:
          'If the boar takes 7 damage or less that would reduce it to 0 hit points, it is reduced to 1 hit point instead.',
      },
    ],
    actions: [
      {
        name: 'Tusk',
        description: 'Melee Weapon Attack: +3 to hit, reach 5 ft., one target.',
        attackBonus: 3,
        damage: '1d6 + 1',
        damageType: 'slashing',
      },
    ],
    wildShapeEligible: true,
    source: 'srd-5.1',
  },
  {
    id: 'giant-badger',
    name: 'Giant Badger',
    size: 'medium',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 10,
    hitPoints: { average: 13, formula: '2d8 + 4' },
    speed: { walk: 30, burrow: 10 },
    abilityScores: {
      strength: 13,
      dexterity: 10,
      constitution: 15,
      intelligence: 2,
      wisdom: 12,
      charisma: 5,
    },
    challengeRating: '1/4',
    experiencePoints: 50,
    senses: ['Darkvision 30 ft.'],
    traits: [
      {
        name: 'Keen Smell',
        description:
          'The badger has advantage on Wisdom (Perception) checks that rely on smell.',
      },
    ],
    actions: [
      {
        name: 'Multiattack',
        description: 'The badger makes two attacks: one with its bite and one with its claws.',
      },
      {
        name: 'Bite',
        description: 'Melee Weapon Attack: +3 to hit, reach 5 ft., one target.',
        attackBonus: 3,
        damage: '1d6 + 1',
        damageType: 'piercing',
      },
      {
        name: 'Claws',
        description: 'Melee Weapon Attack: +3 to hit, reach 5 ft., one target.',
        attackBonus: 3,
        damage: '2d4 + 1',
        damageType: 'slashing',
      },
    ],
    wildShapeEligible: true,
    source: 'srd-5.1',
  },
  {
    id: 'panther',
    name: 'Panther',
    size: 'medium',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 12,
    hitPoints: { average: 13, formula: '3d8' },
    speed: { walk: 50, climb: 40 },
    abilityScores: {
      strength: 14,
      dexterity: 15,
      constitution: 10,
      intelligence: 3,
      wisdom: 14,
      charisma: 7,
    },
    challengeRating: '1/4',
    experiencePoints: 50,
    traits: [
      {
        name: 'Keen Smell',
        description:
          'The panther has advantage on Wisdom (Perception) checks that rely on smell.',
      },
      {
        name: 'Pounce',
        description:
          'If the panther moves at least 20 feet straight toward a creature and then hits it with a claw attack on the same turn, that target must succeed on a DC 12 Strength saving throw or be knocked prone. If the target is prone, the panther can make one bite attack against it as a bonus action.',
      },
    ],
    actions: [
      {
        name: 'Bite',
        description: 'Melee Weapon Attack: +4 to hit, reach 5 ft., one target.',
        attackBonus: 4,
        damage: '1d6 + 2',
        damageType: 'piercing',
      },
      {
        name: 'Claw',
        description: 'Melee Weapon Attack: +4 to hit, reach 5 ft., one target.',
        attackBonus: 4,
        damage: '1d4 + 2',
        damageType: 'slashing',
      },
    ],
    wildShapeEligible: true,
    source: 'srd-5.1',
  },

  // ============================================
  // CR 1/2 Beasts (Level 4 Wild Shape)
  // ============================================
  {
    id: 'black-bear',
    name: 'Black Bear',
    size: 'medium',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 11,
    hitPoints: { average: 19, formula: '3d8 + 6' },
    speed: { walk: 40, climb: 30 },
    abilityScores: {
      strength: 15,
      dexterity: 10,
      constitution: 14,
      intelligence: 2,
      wisdom: 12,
      charisma: 7,
    },
    challengeRating: '1/2',
    experiencePoints: 100,
    traits: [
      {
        name: 'Keen Smell',
        description: 'The bear has advantage on Wisdom (Perception) checks that rely on smell.',
      },
    ],
    actions: [
      {
        name: 'Multiattack',
        description: 'The bear makes two attacks: one with its bite and one with its claws.',
      },
      {
        name: 'Bite',
        description: 'Melee Weapon Attack: +4 to hit, reach 5 ft., one target.',
        attackBonus: 4,
        damage: '1d6 + 2',
        damageType: 'piercing',
      },
      {
        name: 'Claws',
        description: 'Melee Weapon Attack: +4 to hit, reach 5 ft., one target.',
        attackBonus: 4,
        damage: '2d4 + 2',
        damageType: 'slashing',
      },
    ],
    wildShapeEligible: true,
    source: 'srd-5.1',
  },
  {
    id: 'crocodile',
    name: 'Crocodile',
    size: 'large',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 12,
    hitPoints: { average: 19, formula: '3d10 + 3' },
    speed: { walk: 20, swim: 30 },
    abilityScores: {
      strength: 15,
      dexterity: 10,
      constitution: 13,
      intelligence: 2,
      wisdom: 10,
      charisma: 5,
    },
    challengeRating: '1/2',
    experiencePoints: 100,
    traits: [
      {
        name: 'Hold Breath',
        description: 'The crocodile can hold its breath for 15 minutes.',
      },
    ],
    actions: [
      {
        name: 'Bite',
        description:
          'Melee Weapon Attack: +4 to hit, reach 5 ft., one creature. Hit: 7 (1d10 + 2) piercing damage, and the target is grappled (escape DC 12). Until this grapple ends, the target is restrained, and the crocodile can\'t bite another target.',
        attackBonus: 4,
        damage: '1d10 + 2',
        damageType: 'piercing',
      },
    ],
    wildShapeEligible: true,
    source: 'srd-5.1',
  },
  {
    id: 'warhorse',
    name: 'Warhorse',
    size: 'large',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 11,
    hitPoints: { average: 19, formula: '3d10 + 3' },
    speed: { walk: 60 },
    abilityScores: {
      strength: 18,
      dexterity: 12,
      constitution: 13,
      intelligence: 2,
      wisdom: 12,
      charisma: 7,
    },
    challengeRating: '1/2',
    experiencePoints: 100,
    traits: [
      {
        name: 'Trampling Charge',
        description:
          'If the horse moves at least 20 feet straight toward a creature and then hits it with a hooves attack on the same turn, that target must succeed on a DC 14 Strength saving throw or be knocked prone. If the target is prone, the horse can make another attack with its hooves against it as a bonus action.',
      },
    ],
    actions: [
      {
        name: 'Hooves',
        description: 'Melee Weapon Attack: +6 to hit, reach 5 ft., one target.',
        attackBonus: 6,
        damage: '2d6 + 4',
        damageType: 'bludgeoning',
      },
    ],
    wildShapeEligible: true,
    source: 'srd-5.1',
  },

  // ============================================
  // CR 1 Beasts (Level 8 Wild Shape)
  // ============================================
  {
    id: 'brown-bear',
    name: 'Brown Bear',
    size: 'large',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 11,
    hitPoints: { average: 34, formula: '4d10 + 12' },
    speed: { walk: 40, climb: 30 },
    abilityScores: {
      strength: 19,
      dexterity: 10,
      constitution: 16,
      intelligence: 2,
      wisdom: 13,
      charisma: 7,
    },
    challengeRating: '1',
    experiencePoints: 200,
    traits: [
      {
        name: 'Keen Smell',
        description: 'The bear has advantage on Wisdom (Perception) checks that rely on smell.',
      },
    ],
    actions: [
      {
        name: 'Multiattack',
        description: 'The bear makes two attacks: one with its bite and one with its claws.',
      },
      {
        name: 'Bite',
        description: 'Melee Weapon Attack: +6 to hit, reach 5 ft., one target.',
        attackBonus: 6,
        damage: '1d8 + 4',
        damageType: 'piercing',
      },
      {
        name: 'Claws',
        description: 'Melee Weapon Attack: +6 to hit, reach 5 ft., one target.',
        attackBonus: 6,
        damage: '2d6 + 4',
        damageType: 'slashing',
      },
    ],
    wildShapeEligible: true,
    source: 'srd-5.1',
  },
  {
    id: 'dire-wolf',
    name: 'Dire Wolf',
    size: 'large',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 14,
    hitPoints: { average: 37, formula: '5d10 + 10' },
    speed: { walk: 50 },
    abilityScores: {
      strength: 17,
      dexterity: 15,
      constitution: 15,
      intelligence: 3,
      wisdom: 12,
      charisma: 7,
    },
    challengeRating: '1',
    experiencePoints: 200,
    traits: [
      {
        name: 'Keen Hearing and Smell',
        description:
          'The wolf has advantage on Wisdom (Perception) checks that rely on hearing or smell.',
      },
      {
        name: 'Pack Tactics',
        description:
          "The wolf has advantage on an attack roll against a creature if at least one of the wolf's allies is within 5 feet of the creature and the ally isn't incapacitated.",
      },
    ],
    actions: [
      {
        name: 'Bite',
        description:
          'Melee Weapon Attack: +5 to hit, reach 5 ft., one target. Hit: 10 (2d6 + 3) piercing damage. If the target is a creature, it must succeed on a DC 13 Strength saving throw or be knocked prone.',
        attackBonus: 5,
        damage: '2d6 + 3',
        damageType: 'piercing',
      },
    ],
    wildShapeEligible: true,
    source: 'srd-5.1',
  },
  {
    id: 'giant-eagle',
    name: 'Giant Eagle',
    size: 'large',
    type: 'beast',
    alignment: 'neutral good',
    armorClass: 13,
    hitPoints: { average: 26, formula: '4d10 + 4' },
    speed: { walk: 10, fly: 80 },
    abilityScores: {
      strength: 16,
      dexterity: 17,
      constitution: 13,
      intelligence: 8,
      wisdom: 14,
      charisma: 10,
    },
    challengeRating: '1',
    experiencePoints: 200,
    languages: ['Giant Eagle', 'understands Common and Auran but cannot speak them'],
    traits: [
      {
        name: 'Keen Sight',
        description:
          'The eagle has advantage on Wisdom (Perception) checks that rely on sight.',
      },
    ],
    actions: [
      {
        name: 'Multiattack',
        description: 'The eagle makes two attacks: one with its beak and one with its talons.',
      },
      {
        name: 'Beak',
        description: 'Melee Weapon Attack: +5 to hit, reach 5 ft., one target.',
        attackBonus: 5,
        damage: '1d6 + 3',
        damageType: 'piercing',
      },
      {
        name: 'Talons',
        description: 'Melee Weapon Attack: +5 to hit, reach 5 ft., one target.',
        attackBonus: 5,
        damage: '2d6 + 3',
        damageType: 'slashing',
      },
    ],
    wildShapeEligible: true,
    source: 'srd-5.1',
  },
  {
    id: 'giant-spider',
    name: 'Giant Spider',
    size: 'large',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 14,
    hitPoints: { average: 26, formula: '4d10 + 4' },
    speed: { walk: 30, climb: 30 },
    abilityScores: {
      strength: 14,
      dexterity: 16,
      constitution: 12,
      intelligence: 2,
      wisdom: 11,
      charisma: 4,
    },
    challengeRating: '1',
    experiencePoints: 200,
    senses: ['Blindsight 10 ft.', 'Darkvision 60 ft.'],
    traits: [
      {
        name: 'Spider Climb',
        description:
          'The spider can climb difficult surfaces, including upside down on ceilings, without needing to make an ability check.',
      },
      {
        name: 'Web Sense',
        description:
          'While in contact with a web, the spider knows the exact location of any other creature in contact with the same web.',
      },
      {
        name: 'Web Walker',
        description: 'The spider ignores movement restrictions caused by webbing.',
      },
    ],
    actions: [
      {
        name: 'Bite',
        description:
          'Melee Weapon Attack: +5 to hit, reach 5 ft., one creature. Hit: 7 (1d8 + 3) piercing damage, and the target must make a DC 11 Constitution saving throw, taking 9 (2d8) poison damage on a failed save, or half as much on a successful one. If the poison damage reduces the target to 0 hit points, the target is stable but poisoned for 1 hour, even after regaining hit points, and is paralyzed while poisoned in this way.',
        attackBonus: 5,
        damage: '1d8 + 3 + 2d8 poison',
        damageType: 'piercing',
      },
      {
        name: 'Web (Recharge 5-6)',
        description:
          'Ranged Weapon Attack: +5 to hit, range 30/60 ft., one creature. Hit: The target is restrained by webbing. As an action, the restrained target can make a DC 12 Strength check, bursting the webbing on a success.',
        attackBonus: 5,
      },
    ],
    wildShapeEligible: true,
    source: 'srd-5.1',
  },
  {
    id: 'lion',
    name: 'Lion',
    size: 'large',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 12,
    hitPoints: { average: 26, formula: '4d10 + 4' },
    speed: { walk: 50 },
    abilityScores: {
      strength: 17,
      dexterity: 15,
      constitution: 13,
      intelligence: 3,
      wisdom: 12,
      charisma: 8,
    },
    challengeRating: '1',
    experiencePoints: 200,
    traits: [
      {
        name: 'Keen Smell',
        description: 'The lion has advantage on Wisdom (Perception) checks that rely on smell.',
      },
      {
        name: 'Pack Tactics',
        description:
          "The lion has advantage on an attack roll against a creature if at least one of the lion's allies is within 5 feet of the creature and the ally isn't incapacitated.",
      },
      {
        name: 'Pounce',
        description:
          'If the lion moves at least 20 feet straight toward a creature and then hits it with a claw attack on the same turn, that target must succeed on a DC 13 Strength saving throw or be knocked prone. If the target is prone, the lion can make one bite attack against it as a bonus action.',
      },
      {
        name: 'Running Leap',
        description:
          'With a 10-foot running start, the lion can long jump up to 25 feet.',
      },
    ],
    actions: [
      {
        name: 'Bite',
        description: 'Melee Weapon Attack: +5 to hit, reach 5 ft., one target.',
        attackBonus: 5,
        damage: '1d8 + 3',
        damageType: 'piercing',
      },
      {
        name: 'Claw',
        description: 'Melee Weapon Attack: +5 to hit, reach 5 ft., one target.',
        attackBonus: 5,
        damage: '1d6 + 3',
        damageType: 'slashing',
      },
    ],
    wildShapeEligible: true,
    source: 'srd-5.1',
  },
  {
    id: 'tiger',
    name: 'Tiger',
    size: 'large',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 12,
    hitPoints: { average: 37, formula: '5d10 + 10' },
    speed: { walk: 40 },
    abilityScores: {
      strength: 17,
      dexterity: 15,
      constitution: 14,
      intelligence: 3,
      wisdom: 12,
      charisma: 8,
    },
    challengeRating: '1',
    experiencePoints: 200,
    senses: ['Darkvision 60 ft.'],
    traits: [
      {
        name: 'Keen Smell',
        description: 'The tiger has advantage on Wisdom (Perception) checks that rely on smell.',
      },
      {
        name: 'Pounce',
        description:
          'If the tiger moves at least 20 feet straight toward a creature and then hits it with a claw attack on the same turn, that target must succeed on a DC 13 Strength saving throw or be knocked prone. If the target is prone, the tiger can make one bite attack against it as a bonus action.',
      },
    ],
    actions: [
      {
        name: 'Bite',
        description: 'Melee Weapon Attack: +5 to hit, reach 5 ft., one target.',
        attackBonus: 5,
        damage: '1d10 + 3',
        damageType: 'piercing',
      },
      {
        name: 'Claw',
        description: 'Melee Weapon Attack: +5 to hit, reach 5 ft., one target.',
        attackBonus: 5,
        damage: '1d8 + 3',
        damageType: 'slashing',
      },
    ],
    wildShapeEligible: true,
    source: 'srd-5.1',
  },

  // ============================================
  // CR 2 Beasts (Higher level Wild Shape)
  // ============================================
  {
    id: 'giant-boar',
    name: 'Giant Boar',
    size: 'large',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 12,
    hitPoints: { average: 42, formula: '5d10 + 15' },
    speed: { walk: 40 },
    abilityScores: {
      strength: 17,
      dexterity: 10,
      constitution: 16,
      intelligence: 2,
      wisdom: 7,
      charisma: 5,
    },
    challengeRating: '2',
    experiencePoints: 450,
    traits: [
      {
        name: 'Charge',
        description:
          'If the boar moves at least 20 feet straight toward a target and then hits it with a tusk attack on the same turn, the target takes an extra 7 (2d6) slashing damage. If the target is a creature, it must succeed on a DC 13 Strength saving throw or be knocked prone.',
      },
      {
        name: 'Relentless',
        description:
          'If the boar takes 10 damage or less that would reduce it to 0 hit points, it is reduced to 1 hit point instead.',
      },
    ],
    actions: [
      {
        name: 'Tusk',
        description: 'Melee Weapon Attack: +5 to hit, reach 5 ft., one target.',
        attackBonus: 5,
        damage: '2d6 + 3',
        damageType: 'slashing',
      },
    ],
    wildShapeEligible: true,
    source: 'srd-5.1',
  },
  {
    id: 'giant-constrictor-snake',
    name: 'Giant Constrictor Snake',
    size: 'huge',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 12,
    hitPoints: { average: 60, formula: '8d12 + 8' },
    speed: { walk: 30, swim: 30 },
    abilityScores: {
      strength: 19,
      dexterity: 14,
      constitution: 12,
      intelligence: 1,
      wisdom: 10,
      charisma: 3,
    },
    challengeRating: '2',
    experiencePoints: 450,
    senses: ['Blindsight 10 ft.'],
    actions: [
      {
        name: 'Bite',
        description: 'Melee Weapon Attack: +6 to hit, reach 10 ft., one creature.',
        attackBonus: 6,
        damage: '2d6 + 4',
        damageType: 'piercing',
      },
      {
        name: 'Constrict',
        description:
          'Melee Weapon Attack: +6 to hit, reach 5 ft., one creature. Hit: 13 (2d8 + 4) bludgeoning damage, and the target is grappled (escape DC 16). Until this grapple ends, the creature is restrained, and the snake can\'t constrict another target.',
        attackBonus: 6,
        damage: '2d8 + 4',
        damageType: 'bludgeoning',
      },
    ],
    wildShapeEligible: true,
    source: 'srd-5.1',
  },
  {
    id: 'polar-bear',
    name: 'Polar Bear',
    size: 'large',
    type: 'beast',
    alignment: 'unaligned',
    armorClass: 12,
    hitPoints: { average: 42, formula: '5d10 + 15' },
    speed: { walk: 40, swim: 30 },
    abilityScores: {
      strength: 20,
      dexterity: 10,
      constitution: 16,
      intelligence: 2,
      wisdom: 13,
      charisma: 7,
    },
    challengeRating: '2',
    experiencePoints: 450,
    traits: [
      {
        name: 'Keen Smell',
        description:
          'The bear has advantage on Wisdom (Perception) checks that rely on smell.',
      },
    ],
    actions: [
      {
        name: 'Multiattack',
        description: 'The bear makes two attacks: one with its bite and one with its claws.',
      },
      {
        name: 'Bite',
        description: 'Melee Weapon Attack: +7 to hit, reach 5 ft., one target.',
        attackBonus: 7,
        damage: '1d8 + 5',
        damageType: 'piercing',
      },
      {
        name: 'Claws',
        description: 'Melee Weapon Attack: +7 to hit, reach 5 ft., one target.',
        attackBonus: 7,
        damage: '2d6 + 5',
        damageType: 'slashing',
      },
    ],
    wildShapeEligible: true,
    source: 'srd-5.1',
  },
];

// Lookup map for efficient access
export const beastMap = new Map<string, Beast>(beasts.map((b) => [b.id, b]));

// Helper function
export function getBeast(id: string): Beast | undefined {
  return beastMap.get(id);
}

/**
 * Get all beasts that can be familiars
 */
export function getFamiliars(): Beast[] {
  return beasts.filter((b) => b.canBeFamiliar);
}

/**
 * Get beasts eligible for Wild Shape at a given druid level
 */
export function getWildShapeOptions(druidLevel: number): Beast[] {
  // Determine max CR based on druid level
  let maxCR: number;
  let canFly: boolean;
  let canSwim: boolean;

  if (druidLevel >= 8) {
    maxCR = 1;
    canFly = true;
    canSwim = true;
  } else if (druidLevel >= 4) {
    maxCR = 0.5;
    canFly = false;
    canSwim = true;
  } else {
    maxCR = 0.25;
    canFly = false;
    canSwim = false;
  }

  return beasts.filter((beast) => {
    if (!beast.wildShapeEligible) return false;

    const crValue = CR_VALUES[beast.challengeRating];
    if (crValue > maxCR) return false;

    // Check movement restrictions
    if (!canFly && beast.speed.fly) return false;
    if (!canSwim && beast.speed.swim && !beast.speed.walk) return false;

    return true;
  });
}

/**
 * Get beasts by challenge rating
 */
export function getBeastsByCR(cr: ChallengeRating): Beast[] {
  return beasts.filter((b) => b.challengeRating === cr);
}

/**
 * Sort beasts by CR (ascending)
 */
export function sortBeastsByCR(beastList: Beast[]): Beast[] {
  return [...beastList].sort(
    (a, b) => CR_VALUES[a.challengeRating] - CR_VALUES[b.challengeRating]
  );
}
